// Quote/object-link helpers (FEP-e232).
//
// A post can reference several others. Mitra exposes every referenced post as a
// full Status in the custom `links` array (`quote` / `pleroma.quote` are just
// its first entry), and parses the `[[url|label]]` microsyntax in a markdown
// post source into those links, rendering each as a plain anchor in the content
// HTML. chan-fe leans on that: references render as imageboard `>>ref`
// quotelinks in the body. Index and thread views expand metadata-backed quotes
// beneath their references; nested quotes remain click-to-expand.
//
// Servers that only understand a single quote (Pleroma, Mastodon) keep the
// first link and leave the rest as ordinary anchors, which is the same thing we
// render, so the degradation is graceful in both directions.
//
// Pure functions plus one bounded lookup index: no DOM, no network.

// How many digits make up a `>>ref`. Nine, so a reference reads like a post
// number on an imageboard rather than like a hash.
export const POST_REF_LEN = 9;
export const POST_REF_PATTERN = `[1-9][0-9]{${POST_REF_LEN - 1}}`;

// Mitra's LINK_LIMIT (mitra_validators/src/posts.rs) — the number of object
// links it will accept on one post.
export const LINK_LIMIT = 10;

// MediaWiki-like object link syntax, matching Mitra's OBJECT_LINK_SEARCH_RE.
const OBJECT_LINK_RE = /\[\[(?<url>[^\s|]+?)(\|(?<text>.+?))?\]\]/g;

// ---- post refs --------------------------------------------------------------
//
// A `>>ref` names a post, and has to name the same post on every server. It
// used to be the tail of the status id, which is issued by whichever server
// you were signed into — a FlakeId on Pleroma, a ULID on Mitra — so a
// reference written from one account named nothing when read from another.
//
// The ref is now derived from the post's ActivityPub object id, the one name
// every server agrees on. Same post, same ref, wherever it's read.
//
// The hash has to be synchronous: refs are built while rendering, and Web
// Crypto's digest is async. cyrb53 is a well-known non-cryptographic 53-bit
// string hash; nothing here needs to resist an adversary, only collisions
// among the posts on screen, and nine digits is ~30 bits of it — about one
// chance in fifty thousand that a 200-post thread contains a colliding pair.
function cyrb53(str) {
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

// Refs are drawn from [100000000, 999999999]: a fixed width, and never a
// leading zero, so every one of them looks like a real post number.
const REF_MIN = 10 ** (POST_REF_LEN - 1);
const REF_SPAN = 9 * REF_MIN;

const hashCache = new Map();
const HASH_CACHE_MAX = 5000;

// Refs are rebuilt on every render and compared against every post on screen,
// so the hash is memoized per object id.
function hashForObjectUrl(url) {
  let h = hashCache.get(url);
  if (h !== undefined) return h;
  h = cyrb53(url);
  if (hashCache.size >= HASH_CACHE_MAX) hashCache.delete(hashCache.keys().next().value);
  hashCache.set(url, h);
  return h;
}

// The `>>ref` for a status. Takes the status, not an id — the id alone doesn't
// carry the object id this is derived from. A post without an object URL
// cannot have a reference yet.
export function shortPostRef(status) {
  return refForObjectUrl(objectUrlFor(status));
}

// The `>>ref` an object URL names. Same hash as shortPostRef, from the URL
// alone — so a link can be matched against a reference written beside it
// without either one resolving to a loaded post (see absorbRefMentions).
export function refForObjectUrl(url) {
  const norm = normalizeObjectUrl(url);
  if (!norm) return "";
  return String(REF_MIN + (hashForObjectUrl(norm) % REF_SPAN));
}

// Every post this one references, in order. Mitra sends them all; other servers
// send at most one, under `quote` (Mastodon's shape wraps it in `quoted_status`)
// or Pleroma's `pleroma.quote`.
export function quotedStatuses(status) {
  // Prefer the multi-link representation, but do not make it exclusive. A
  // few Mastodon-compatible servers expose an unrelated/partial `links`
  // array alongside their native single-quote field. Treating the mere
  // presence of that array as authoritative used to hide the usable quote.
  const candidates = [
    ...(Array.isArray(status?.links) ? status.links : []),
    status?.quote?.quoted_status || status?.quote,
    status?.pleroma?.quote,
  ];

  const out = [];
  const seen = new Set();
  for (const s of candidates) {
    if (!s || typeof s !== "object" || !s.id || seen.has(s.id)) continue;
    seen.add(s.id);
    out.push(s);
  }
  return out;
}

// The AP object id of a status — what `[[…]]` links have to point at for the
// server to resolve them (`status.url` is a human-facing permalink and may
// differ, so it's only a fallback).
export function objectUrlFor(status) {
  return status?.uri || status?.url || "";
}

// Compare-friendly form of an object URL: trailing slashes are insignificant
// and the origin is case-insensitive, but the path is not.
export function normalizeObjectUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  try {
    const u = new URL(raw);
    return (u.origin.toLowerCase() + u.pathname.replace(/\/+$/, "") + u.search).replace(/\/+$/, "");
  } catch {
    return raw.replace(/\/+$/, "");
  }
}

// ---- quoted-status index --------------------------------------------------
//
// Referenced statuses arrive inline on their referencing post, but the anchors
// that point at them are wired up per container, long after the post object is
// out of scope — and a `>>ref` may well be typed in a *different* post than the
// one carrying the link. So rendered quotes go into a small shared index,
// keyed by everything an anchor could be matched on.

const QUOTE_INDEX_MAX = 500;
const quoteIndex = new Map();

function indexOne(key, status) {
  if (!key) return;
  // Re-inserting refreshes recency, so a post that keeps rendering is never
  // the one evicted.
  quoteIndex.delete(key);
  quoteIndex.set(key, status);
  while (quoteIndex.size > QUOTE_INDEX_MAX) {
    quoteIndex.delete(quoteIndex.keys().next().value);
  }
}

// Register one status under every key an anchor or a `>>ref` could name it by.
export function rememberQuoted(status) {
  if (!status?.id) return;
  indexOne("id:" + status.id, status);
  const ref = shortPostRef(status);
  if (ref) indexOne("ref:" + ref, status);
  if (status.uri) indexOne("url:" + normalizeObjectUrl(status.uri), status);
  if (status.url && status.url !== status.uri) {
    indexOne("url:" + normalizeObjectUrl(status.url), status);
  }
}

// Register a status's referenced posts. Called when a post renders.
export function indexQuotedStatuses(status) {
  const quoted = quotedStatuses(status);
  for (const q of quoted) rememberQuoted(q);
  return quoted;
}

// Look up a referenced status by anchor href, by `>>ref` token, or by id.
export function lookupQuoted({ href = "", ref = "" } = {}) {
  const byHref = href && quoteIndex.get("url:" + normalizeObjectUrl(href));
  if (byHref) return byHref;
  const token = String(ref || "").trim();
  if (!token) return null;
  return quoteIndex.get("id:" + token) || quoteIndex.get("ref:" + token) || null;
}

// Test seam: the index is process-global, so tests reset it between cases.
export function clearQuoteIndex() {
  quoteIndex.clear();
}

// ---- composing ------------------------------------------------------------

// A `>>ref` written as plain text, the way it's typed on an imageboard. Mitra
// enables comrak's greentext extension, so `>>123456789` at the start of a line
// stays literal text rather than turning into nested blockquotes — which is
// what lets the composer hold bare refs at all.
//
// Only complete nine-digit post numbers match. Existing markdown/object-link
// labels and greentext words stay as written.
const BARE_REF_RE = new RegExp(String.raw`(?<![[|])>>(${POST_REF_PATTERN})(?![\p{L}\p{N}_-])`, "gu");

// Give every bare `>>ref` in a post source a target, so the reference means
// something off this frontend: readers on other servers get a working link
// instead of an opaque short id. `resolveUrl(ref)` returns the referenced
// post's object URL, or a falsy value to leave the ref alone.
//
// A ref whose target already follows it is left alone: it's a complete
// reference as it stands. That's how a reference reads on a server that
// renders no markdown (see postbody.js), so it's what comes back when editing
// a post there — and linking it again would publish the URL twice.
export function expandBarePostRefs(text, resolveUrl) {
  const src = String(text || "");
  return src.replace(BARE_REF_RE, (match, ref, offset) => {
    const url = resolveUrl(ref);
    if (!url) return match;
    // The next word on the same line, so it's the ref's own trailing URL and
    // not one that merely turns up later in the post.
    const trailing = /^[ \t]+(\S+)/.exec(src.slice(offset + match.length));
    if (trailing?.[1] === url) return match;
    return `[>>${ref}](${url})`;
  });
}

// The reference chan-fe writes into a post source. `[[url|>>ref]]` is Mitra's
// object-link microsyntax: the server resolves it to a real quote and renders
// the `>>ref` label as the anchor text. Everywhere else it has to stay an
// ordinary markdown link, which federates as a plain link but still reads as a
// quotelink in chan-fe (both render as an anchor labelled `>>ref`).
export function barePostRef(status) {
  const ref = status?.id && shortPostRef(status);
  return ref ? ">>" + ref : "";
}

// Traditional fediverse reply-all prefill: address the post author and every
// account the post already mentions, except the signed-in user. Account ids
// are authoritative; acct is also tracked so the author isn't repeated when
// the status's mentions array names the same account with a separate object.
export function replyMentionPrefill(status, ownAccount) {
  const ownId = ownAccount?.id == null ? "" : String(ownAccount.id);
  const ownAcct = mentionAcct(ownAccount).toLowerCase();
  const seenIds = new Set();
  const seenAccts = new Set();
  const mentions = [];
  const accounts = [status?.account, ...(Array.isArray(status?.mentions) ? status.mentions : [])];

  for (const account of accounts) {
    const acct = mentionAcct(account);
    if (!acct) continue;
    const id = account?.id == null ? "" : String(account.id);
    const acctKey = acct.toLowerCase();
    if ((ownId && id === ownId) || (ownAcct && acctKey === ownAcct)) continue;
    if ((id && seenIds.has(id)) || seenAccts.has(acctKey)) continue;
    if (id) seenIds.add(id);
    seenAccts.add(acctKey);
    mentions.push(`@${acct}`);
  }

  return mentions.length ? mentions.join(" ") + " " : "";
}

// Toggle the generated reply prefix while preserving the body and manually
// added mentions. Only previously inserted handles in the leading mention
// run are removed; handles already there are not duplicated when enabling.
export function setReplyMentionPrefill(text, prefill, enabled, insertedPrefix = "") {
  const source = String(text || "");
  const leading = source.match(/^(?:@\S+(?:[ \t]+|(?=[\r\n]|$)))+/)?.[0] || "";
  const inserted = new Set(insertedPrefix.trim().toLowerCase().split(/\s+/));
  const kept = leading.replace(/@\S+(?:[ \t]+|(?=[\r\n]|$))/g, token =>
    inserted.has(token.trim().toLowerCase()) ? "" : token);
  const existing = new Set(kept.trim().toLowerCase().split(/\s+/));
  const mentions = enabled ? prefill.trim().split(/\s+/).filter(token =>
    token && !existing.has(token.toLowerCase())) : [];
  const prefix = mentions.length ? mentions.join(" ") + " " : "";
  return { text: prefix + kept + source.slice(leading.length), prefix };
}

function mentionAcct(account) {
  // A real Mastodon Account/Mention entity has a username. Requiring it keeps
  // bare-id placeholders used by unresolved >>refs out of reply text.
  if (!account?.username) return "";
  const acct = String(account.acct || account.username).trim().replace(/^@+/, "");
  return /^[^\s@]+(?:@[^\s@]+)?$/.test(acct) ? acct : "";
}

export function quoteRefMarkdown(status, { microsyntax = false } = {}) {
  const url = objectUrlFor(status);
  if (!url) return "";
  const ref = ">>" + shortPostRef(status);
  return microsyntax ? `[[${url}|${ref}]]` : `[${ref}](${url})`;
}

export function countObjectLinks(text) {
  return Array.from(String(text || "").matchAll(OBJECT_LINK_RE)).length;
}

// Rewrite `[[url|label]]` into `[label](url)` for servers that don't parse the
// microsyntax, and for posts that aren't allowed to carry object links at all
// (Mitra rejects links on non-public posts). Leaves the label's `>>ref` intact,
// so the post still reads the same on both ends.
export function downgradeObjectLinks(text) {
  return String(text || "").replace(OBJECT_LINK_RE, (_m, ...args) => {
    const groups = args[args.length - 1];
    const url = groups.url;
    return groups.text ? `[${groups.text}](${url})` : url;
  });
}

// Only Mitra parses `[[…]]` server-side. Its Mastodon API reports itself as
// e.g. "4.0.0 (compatible; Mitra 5.8.0)".
export function isMitraServer(instance) {
  return /\(compatible;\s*Mitra[\s/]/i.test(String(instance?.version || ""));
}

// Mitra 5.11.0 lifted the restriction on the quoting post's audience.
// Unknown versions retain the older server's public-only behavior.
export function supportsNonPublicMitraQuotes(instance) {
  if (!isMitraServer(instance)) return false;
  const version = /\bMitra[\s/]+(\d+)\.(\d+)\.(\d+)(?=[\s)+]|$)/i.exec(String(instance.version));
  if (!version) return false;
  const [, major, minor] = version.map(Number);
  return major > 5 || (major === 5 && minor >= 11);
}

// The quoted target must still be public (`can_link_post`).
export function isQuotableStatus(status) {
  const v = status?.visibility;
  return !v || v === "public" || v === "unlisted";
}
